import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowLeft, User, Calendar, Clock, AlertTriangle, CheckCircle,
  ChevronDown, ChevronRight, MapPin, FileSpreadsheet, XCircle, RefreshCw
} from 'lucide-react';

const UploadDetailView = ({ uploadId, onBack, embedded = false, searchFilter = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [expandedShifts, setExpandedShifts] = useState({});
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'violations', 'passed'
  const [redetecting, setRedetecting] = useState(false);

  useEffect(() => {
    fetchUploadDetails();
    // Reset UI state when upload changes
    setExpandedEmployees({});
    setExpandedShifts({});
    setFilterStatus('all');
  }, [uploadId]);

  const fetchUploadDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      let url;
      if (uploadId === 'all') {
        url = '/api/time-audit/employees/all';
      } else {
        url = `/api/time-audit/uploads/${uploadId}/details`;
      }
      const response = await axios.get(url);
      setData(response.data);

      // Auto-expand first few employees
      const autoExpand = {};
      response.data.employees.slice(0, 3).forEach(emp => {
        autoExpand[emp.employeeId] = true;
      });
      setExpandedEmployees(autoExpand);
    } catch (err) {
      console.error('Error fetching upload details:', err);
      setError(err.response?.data?.error || 'Failed to load upload details');
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (employeeId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  const handleToggleSalaried = async (employee, e) => {
    e.stopPropagation();
    const locationId = employee.locationId || data.upload?.location?.id;
    if (!locationId) {
      console.error('No locationId available for employee', employee.employeeId);
      return;
    }
    try {
      if (!employee.isSalaried) {
        const resolvedCount = (employee.shifts || []).reduce(
          (sum, s) => sum + (s.violations || []).filter(v => v.status && v.status !== 'pending').length,
          0
        );
        const resolvedWarning = resolvedCount > 0
          ? `\n\n⚠️  This will also permanently delete ${resolvedCount} resolved violation(s) visible here (and any others at this location across all uploads), including any manager notes and resolutions.`
          : '\n\nNote: any resolved violations for this employee at this location will also be deleted.';
        const confirmed = window.confirm(
          `Mark ${employee.employeeName} as salaried (exempt)? All their violations at this location will be removed.${resolvedWarning}`
        );
        if (!confirmed) return;
        // Mark as salaried
        await axios.post('/api/time-audit/salaried-employees', {
          employeeId: employee.employeeId,
          locationId,
          employeeName: employee.employeeName
        });
      } else {
        // Find the salaried record to delete
        const response = await axios.get(`/api/time-audit/salaried-employees?locationId=${locationId}`);
        const record = response.data.salariedEmployees.find(s => s.employeeId === employee.employeeId);
        if (record) {
          await axios.delete(`/api/time-audit/salaried-employees/${record.id}`);
        }
      }
      // Refresh data to reflect changes
      fetchUploadDetails();
    } catch (err) {
      console.error('Error toggling salaried status:', err);
    }
  };

  const handleRedetect = async () => {
    if (uploadId === 'all') return;
    const confirmed = window.confirm(
      'Re-run violation detection for this upload? Pending violations will be regenerated from current data. Resolved violations (valid, corrected) and manager responses are preserved.'
    );
    if (!confirmed) return;
    try {
      setRedetecting(true);
      const response = await axios.post(`/api/time-audit/uploads/${uploadId}/redetect`);
      const { deletedPending, preservedResolved, newlyCreated, totalViolations } = response.data;
      alert(
        `Re-detection complete:\n` +
        `• Newly created: ${newlyCreated}\n` +
        `• Preserved (resolved): ${preservedResolved}\n` +
        `• Pending replaced: ${deletedPending}\n` +
        `• Total now: ${totalViolations}`
      );
      await fetchUploadDetails();
    } catch (err) {
      console.error('Error re-running detection:', err);
      alert(`Re-detection failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setRedetecting(false);
    }
  };

  const toggleShift = (employeeId, workDate) => {
    const key = `${employeeId}-${workDate}`;
    setExpandedShifts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDuration = (hours) => {
    if (!hours) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const getViolationBadge = (violations) => {
    if (!violations || violations.length === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle size={12} />
          Passed
        </span>
      );
    }

    const pending = violations.filter(v => v.status === 'pending').length;
    const total = violations.length;

    if (pending === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <CheckCircle size={12} />
          {total} Resolved
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertTriangle size={12} />
        {pending} Pending
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      valid: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
      corrected: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }
    };
    const style = config[status] || config.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon size={10} />
        {status}
      </span>
    );
  };

  const getEntryTypeStyle = (entryType) => {
    switch (entryType) {
      case 'work':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'unpaid_break':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'paid_break':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const formatEntryType = (entryType) => {
    switch (entryType) {
      case 'work': return 'Work';
      case 'unpaid_break': return 'Unpaid Break';
      case 'paid_break': return 'Paid Break';
      default: return entryType;
    }
  };

  const formatViolationType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter employees based on violation status
  const filteredEmployees = data?.employees.filter(emp => {
    // Apply search filter
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      const nameMatch = (emp.employeeName || '').toLowerCase().includes(search);
      const idMatch = (emp.employeeId || '').toLowerCase().includes(search);
      if (!nameMatch && !idMatch) return false;
    }

    // Apply status filter
    if (filterStatus === 'all') return true;

    const hasViolations = emp.shifts.some(s => s.violations && s.violations.length > 0);

    if (filterStatus === 'violations') return hasViolations;
    if (filterStatus === 'passed') return !hasViolations;

    return true;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading upload details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">{error}</p>
        {!embedded && onBack && (
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Go Back
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header - hidden when embedded in tab */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to uploads"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">{data.upload.originalFileName}</h2>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                {data.upload.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {data.upload.location.locationNumber} - {data.upload.location.locationName}
                  </span>
                )}
                {data.upload.payPeriodStart && data.upload.payPeriodEnd && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(data.upload.payPeriodStart).toLocaleDateString('en-US')} - {new Date(data.upload.payPeriodEnd).toLocaleDateString('en-US')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{data.summary.totalEmployees}</div>
          <div className="text-sm text-gray-600">Employees</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{data.summary.totalShifts}</div>
          <div className="text-sm text-gray-600">Total Shifts</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{data.summary.passedShifts}</div>
          <div className="text-sm text-green-600">Passed</div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="text-2xl font-bold text-red-700">{data.summary.totalViolations}</div>
          <div className="text-sm text-red-600">Violations</div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="text-2xl font-bold text-yellow-700">{data.summary.pendingViolations}</div>
          <div className="text-sm text-yellow-600">Pending</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 items-center">
        {(() => {
          // Apply search filter to get base set for counts
          const searchedEmployees = searchFilter
            ? data.employees.filter(emp => {
                const s = searchFilter.toLowerCase();
                return (emp.employeeName || '').toLowerCase().includes(s) || (emp.employeeId || '').toLowerCase().includes(s);
              })
            : data.employees;
          return [
            { value: 'all', label: `All Employees (${searchedEmployees.length})` },
            { value: 'violations', label: `With Violations (${searchedEmployees.filter(e => e.shifts.some(s => s.violations?.length > 0)).length})` },
            { value: 'passed', label: `All Passed (${searchedEmployees.filter(e => !e.shifts.some(s => s.violations?.length > 0)).length})` }
          ];
        })().map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilterStatus(tab.value)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              filterStatus === tab.value
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
        {uploadId !== 'all' && (
          <button
            onClick={handleRedetect}
            disabled={redetecting}
            className="ml-auto flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete existing violations and re-run detection for this upload"
          >
            <RefreshCw className={`h-4 w-4 ${redetecting ? 'animate-spin' : ''}`} />
            {redetecting ? 'Re-running…' : 'Re-run detection'}
          </button>
        )}
      </div>

      {/* Employee List */}
      <div className="space-y-3">
        {filteredEmployees.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
            No employees match the selected filter.
          </div>
        ) : (
          filteredEmployees.map(employee => {
            const isExpanded = expandedEmployees[employee.employeeId];
            const totalViolations = employee.shifts.reduce((acc, s) => acc + (s.violations?.length || 0), 0);
            const pendingViolations = employee.shifts.reduce((acc, s) =>
              acc + (s.violations?.filter(v => v.status === 'pending').length || 0), 0);

            return (
              <div key={employee.employeeId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Employee Header */}
                <button
                  onClick={() => toggleEmployee(employee.employeeId)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                    <User className="h-5 w-5 text-gray-400" />
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{employee.employeeName || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">ID: {employee.employeeId} • {employee.shifts.length} shifts</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Salaried Toggle */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        employee.isSalaried ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        Salaried
                      </span>
                      <button
                        onClick={(e) => handleToggleSalaried(employee, e)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          employee.isSalaried ? 'bg-purple-500' : 'bg-gray-300'
                        }`}
                        title={employee.isSalaried ? 'Mark as hourly' : 'Mark as salaried (exclude from violations)'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          employee.isSalaried ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                      {employee.isSalaried && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-600">
                          Exempt
                        </span>
                      )}
                    </div>
                    {/* Violation Badge */}
                    {employee.isSalaried ? null : totalViolations > 0 ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        pendingViolations > 0 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        <AlertTriangle size={12} />
                        {pendingViolations > 0 ? `${pendingViolations} pending` : `${totalViolations} resolved`}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle size={12} />
                        All passed
                      </span>
                    )}
                  </div>
                </button>

                {/* Employee Shifts */}
                {isExpanded && (
                  <div className="border-t border-gray-200">
                    {employee.shifts.map(shift => {
                      const shiftKey = `${employee.employeeId}-${shift.workDate}`;
                      const isShiftExpanded = expandedShifts[shiftKey];

                      return (
                        <div key={shift.workDate} className="border-b border-gray-100 last:border-b-0">
                          {/* Shift Header */}
                          <button
                            onClick={() => toggleShift(employee.employeeId, shift.workDate)}
                            className="w-full px-4 py-2 pl-12 flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {isShiftExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                              )}
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-700">{formatDate(shift.workDate)}</span>
                              <span className="text-sm text-gray-500">• {formatDuration(shift.totalHours)} worked</span>
                              <span className="text-xs text-gray-400">({(shift.segments || []).length} segments)</span>
                            </div>
                            <div>
                              {getViolationBadge(shift.violations)}
                            </div>
                          </button>

                          {/* Shift Details */}
                          {isShiftExpanded && (
                            <div className="px-4 py-3 pl-20 bg-gray-50 space-y-3">
                              {/* Segments */}
                              <div className="space-y-2">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Time Segments</div>
                                <div className="grid gap-2">
                                  {(shift.segments || []).map((segment, idx) => (
                                    <div
                                      key={segment.id}
                                      className={`flex items-center justify-between p-2 rounded border ${getEntryTypeStyle(segment.entryType)}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className="text-xs font-medium w-20">{formatEntryType(segment.entryType)}</span>
                                        <Clock size={14} />
                                        <span className="text-sm">
                                          {formatTime(segment.clockIn)} - {formatTime(segment.clockOut)}
                                        </span>
                                        {segment.jobTitle && (
                                          <span className="text-xs text-gray-500">({segment.jobTitle})</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium">
                                          {segment.entryType === 'work'
                                            ? formatDuration(segment.hoursWorked)
                                            : formatDuration(segment.breakDuration)
                                          }
                                        </span>
                                        {segment.violation && (
                                          <AlertTriangle size={14} className="text-red-500" />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Violations for this shift */}
                              {shift.violations && shift.violations.length > 0 && (
                                <div className="space-y-2">
                                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Violations</div>
                                  <div className="grid gap-2">
                                    {shift.violations.map(violation => (
                                      <div
                                        key={violation.id}
                                        className={`p-3 rounded border ${
                                          violation.status === 'pending'
                                            ? 'bg-red-50 border-red-200'
                                            : violation.status === 'corrected'
                                              ? 'bg-green-50 border-green-200'
                                              : 'bg-gray-50 border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <AlertTriangle size={14} className={
                                                violation.status === 'pending' ? 'text-red-500' : 'text-gray-400'
                                              } />
                                              <span className="font-medium text-sm">
                                                {formatViolationType(violation.violationType)}
                                              </span>
                                            </div>
                                            <p className="text-xs text-gray-600 mt-1">{violation.description}</p>
                                            {violation.potentialPenalty > 0 && (
                                              <p className="text-xs text-red-600 mt-1">
                                                Potential penalty: ${parseFloat(violation.potentialPenalty).toFixed(2)}
                                              </p>
                                            )}
                                          </div>
                                          <div>
                                            {getStatusBadge(violation.status)}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Shift Summary */}
                              <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-sm">
                                <span className="text-gray-600">Total Shift Duration:</span>
                                <span className="font-medium text-gray-900">{formatDuration(shift.totalHours)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UploadDetailView;
